<template>
    <div class="regionsDirectoryModal">
        <div
            v-if="regionData"
            class="regionsDirectory__header">
            <button
                v-for="(tab, index) in tabs"
                :key="tab.name"
                type="button"
                :aria-label="tab.name"
                class="regionsDirectory__tabButton hl-module"
                :class="{
                    'regionsDirectory__tabButton--active': tab.name === shownTab,
                    'margin-right-25': index === 0,
                }"
                :data-gtm="tab.dataGtm"
                @keyup.enter="setShownTab(tab.name)"
                @click="setShownTab(tab.name)">
                {{ tab.title }}
            </button>
        </div>
        <template v-if="!regionData">
            <div class="cssPreloader--absolute animated fadeIn z-index-1 width100 text-center">
                <div class="cssPreloader">
                    <div class="cssPreloader__inner"></div>
                </div>
                <div class="h5 subline margin-bottom-0">
                    {{ trans('PLEASE_WAIT') }}
                </div>
            </div>
        </template>
        <div
            v-else
            class="regionsDirectory__tabContent"
            :class="`regionsDirectory__${ shownTab }Content`">
            <template v-if="shownTab === REGIONS">
                <div class="regionsDirectory__item margin-bottom-40">
                    {{ trans('DMO_WORLDWIDE') }}
                </div>
                <span class="regionsDirectory__item bold">
                    {{ trans('POLITICAL_REGIONS') }}
                </span>
                <div
                    v-for="region in regionData.regions.politicalEntity"
                    :key="region.id"
                    class="regionsDirectory__item">
                    {{ region.name }}
                    <Tooltip>
                        <template #default>
                            <span class="cursor-pointer margin-left-0">
                                <svg class="stat__svgIcon--small svg__padding--minus">
                                    <use href="#iconInfoCircle" />
                                </svg>
                            </span>
                        </template>
                        <template #content>
                            <UserRegionTooltipContent
                                v-if="region.children.length"
                                :group-name="getRegionName(region)"
                                :sub-regions="childCountries(region.children)" />
                        </template>
                    </Tooltip>
                </div>
                <span class="regionsDirectory__item bold">
                    {{ trans('CONTINENTS') }}
                </span>
                <div
                    v-for="region in continentsAll"
                    :key="region.id"
                    class="regionsDirectory__item">
                    {{ region.name }}
                    <Tooltip>
                        <template #default>
                            <span class="cursor-pointer margin-left-0">
                                <svg class="stat__svgIcon--small svg__padding--minus">
                                    <use href="#iconInfoCircle" />
                                </svg>
                            </span>
                        </template>
                        <template #content>
                            <UserRegionTooltipContent
                                v-if="region.children.length"
                                :group-name="region.name"
                                :sub-regions="childCountries(region.children)" />
                        </template>
                    </Tooltip>
                </div>
            </template>
            <template v-else>
                <template v-for="(char) in splitLetters.getKeysFromMap()">
                    <span
                        :key="`directoryLetter__${ char }`"
                        class="bold margin-bottom-10"
                        :class="{ 'margin-top-20' : char !== 'A' }">
                        {{ char }}
                    </span>
                    <div
                        v-for="(country) in splitLetters.getItemsByKey(char)"
                        :key="country.id"
                        class="regionsDirectory__item">
                        {{ country.name }}
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
    import Translator from 'tools/trans';
    import { doAjaxRequest, AXIOS_REQUEST_TYPES, AXIOS_RESPONSE_TYPES } from 'outlook/utils/doAxiosRequest';
    import { getPath } from 'tools/getFromHtml';
    import { mapState } from 'vuex';
    import UserRegionTooltipContent from 'outlook/components/filter/userRegionTooltipContent';
    import Tooltip from 'components/tooltip';
    import { SplitByCharacter } from 'outlook/utils/letterSplit';

    const REGIONS = 'regions';
    const COUNTRIES = 'countries';

    export default {
        components: {
            UserRegionTooltipContent,
            Tooltip,
        },
        props: {
            isOpen: {
                type: Boolean,
                required: true,
            },
        },
        data() {
            return {
                REGIONS: REGIONS,
                COUNTRIES: COUNTRIES,
                shownTab: COUNTRIES,
                marketHighlightsRegionsAjaxPath: getPath('outlookHighlightsRegionsAjax'),
                regionData: null,
                tabs: [
                    {
                        name: REGIONS,
                        title: Translator.trans('OUTLOOK_REGIONS'),
                        dataGtm: 'showRegionsTab',
                    },
                    {
                        name: COUNTRIES,
                        title: Translator.trans('FORM_COUNTRIES_TERRITORIES'),
                        dataGtm: 'showCountriesTab',
                    },
                ],
                splitLetters: null,
            };
        },
        computed: {
            ...mapState([
                'currentOutlook',
            ]),
            continentsAll() {
                return this.regionData?.regions?.continent.concat(this.regionData?.regions?.subContinent);
            },
            sortedCountries() {
                const countries = this.regionData?.countries;

                return countries?.sort((a, b) => (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0);
            },
        },
        watch: {
            isOpen(newState) {
                if (newState === true && !this.regionData) {
                    this.getMarketHighlightsRegions();
                }
            },
            regionData() {
                this.splitLetters = new SplitByCharacter(this.sortedCountries);
            },
        },
        methods: {
            trans: Translator.trans,
            doAjaxRequest,
            setShownTab(value) {
                this.shownTab = value;
            },
            getMarketHighlightsRegions() {
                this.doAjaxRequest(AXIOS_REQUEST_TYPES.GET, this.marketHighlightsRegionsAjaxPath, {
                    outlookName: this.currentOutlook.toLocaleLowerCase(),
                    locale: window.stGlobals.locale,
                }).then(({ status, data }) => {
                    if (status === AXIOS_RESPONSE_TYPES.OK) {
                        this.regionData = data;
                    }
                }).catch(error => {
                    console.error(error);
                });
            },
            getCountryById(countryId) {
                return this.regionData.countries.find(country => country.id === countryId);
            },
            childCountries(childrenIds) {
                const childrenCountries = [];
                childrenIds.forEach(childId => {
                    const childCountry = this.getCountryById(childId);
                    if (childCountry) {
                        childrenCountries.push({ name: childCountry.name, id: childId });
                    }
                });

                return childrenCountries;
            },
            getRegionName(region) {
                if (region?.description?.length) {
                    return region.description;
                }

                return region.name;
            },
        },
    };
</script>
